import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatErr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RecoveryCodes } from "@/components/RecoveryCodes";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";

export function MfaSecurityCard() {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [codes, setCodes] = useState(null);

    const load = () => api.get("/mfa/status").then((r) => setStatus(r.data)).catch(() => setStatus({}));
    useEffect(() => { load(); }, []);

    const regen = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/mfa/recovery-codes/regenerate");
            setCodes(data.recovery_codes);
            toast.success("New recovery codes generated");
            load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="mfa-security-card">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2 text-brand-emerald font-semibold"><ShieldCheck className="h-4 w-4 text-brand-teal" /> Two-step verification</div>
            <div className="p-5 space-y-4">
                {!status ? (
                    <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-brand-emerald" /></div>
                ) : (
                    <>
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                            {status.mfa_enabled ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="mfa-status-badge"><ShieldCheck className="h-3.5 w-3.5" /> Enabled</span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200" data-testid="mfa-status-badge"><ShieldAlert className="h-3.5 w-3.5" /> Not set up</span>
                            )}
                            {status.mfa_enabled && <span className="text-muted-foreground" data-testid="recovery-remaining">{status.recovery_codes_remaining} recovery code{status.recovery_codes_remaining === 1 ? "" : "s"} left</span>}
                        </div>
                        {status.mfa_enabled ? (
                            <>
                                <p className="text-sm text-muted-foreground">Generate a fresh set of one-time recovery codes. This immediately invalidates any codes you had before.</p>
                                <Button disabled={busy} onClick={regen} variant="outline" className="rounded-lg" data-testid="regenerate-recovery-button">
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1" /> Regenerate recovery codes</>}
                                </Button>
                            </>
                        ) : (
                            <p className="text-sm text-muted-foreground">You'll set up an authenticator app the next time you sign in. Recovery codes are issued once you're enrolled.</p>
                        )}
                    </>
                )}
            </div>
            <Dialog open={!!codes} onOpenChange={(o) => !o && setCodes(null)}>
                <DialogContent data-testid="recovery-codes-dialog">
                    <DialogHeader><DialogTitle>Save your recovery codes</DialogTitle></DialogHeader>
                    {codes && <RecoveryCodes codes={codes} onDone={() => setCodes(null)} doneLabel="I've saved them" />}
                </DialogContent>
            </Dialog>
        </div>
    );
}
